import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from 'components/Camera/style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Marker from './marker'
import Camera from 'components/Camera'
import ReactDOMServer from 'react-dom/server'
import RenameCameraOverlay from 'components/Camera/RenameCameraOverlay'
import ContextMenu from 'ContextMenu'
import showContextMenu from 'helpers/showContextMenu'
import { CAMERA_COLORS, CAMERA_SELECTED_COLORS, COPY_ICON_STACK } from 'helpers/globals'
import { zoomToSingleCameraAction, addMapComponentReferencesAction } from 'actions/mapActions'
import { updateCalculatorAction } from 'actions/calculatorActions'
import { changeCameraAction, batchUpdateCameraAction } from 'actions/cameraActions'
import { destroyMultiImagerAction } from 'actions/multiImagerActions'
import { updateCurrentEntityAction, checkOfflineLimitsAction } from 'actions/applicationActions'
import { messageAction } from 'actions/messageActions'
import { makeCameraPropSelector, makeMapRefSelector, makeCameraCountSelector, makeCameraSelectedSelector,
  makeLensSelectedSelector, makeUiPropSelector } from './selectors'
import { makeDeviceWireSelectedSelector } from 'selectors/components'
import onMobile from 'helpers/onMobile'

const makeMapStateToProps = _ => {
  const locationSelector = makeCameraPropSelector('location')
  const markersSelector = makeCameraPropSelector('markers')
  const activeSelector = makeCameraPropSelector('active')
  const lockedSelector = makeCameraPropSelector('locked')
  const nameSelector = makeCameraPropSelector('name')
  const newlyAddedSelector = makeCameraPropSelector('newlyAdded')
  const colorSelector = makeCameraPropSelector('color')
  const lensIdSelector = makeCameraPropSelector('lensIds')
  const fixedLensIdSelector = makeCameraPropSelector('fixedLensIds')
  const modelSelector = makeCameraPropSelector('model')
  const mapRefSelector = makeMapRefSelector()
  const cameraCountSelector = makeCameraCountSelector()
  const cameraSelectedSelector = makeCameraSelectedSelector()
  const lensSelectedSelector = makeLensSelectedSelector()
  const cameraViewsChoiceSelector = makeUiPropSelector('cameraViewsChoice')
  const showSimpleCameraIconSelector = makeUiPropSelector('showSimpleCameraIcon')
  const drawingWallSelector = makeUiPropSelector('drawingWall')
  const drawingWireSelector = makeUiPropSelector('drawingWire')
  const wireSelectedSelector = makeDeviceWireSelectedSelector()

  const mapStateToProps = (state, ownProps) => ({
    id: ownProps.id,
    location: locationSelector(state, ownProps),
    markers: markersSelector(state, ownProps),
    active: activeSelector(state, ownProps),
    locked: lockedSelector(state, ownProps),
    name: nameSelector(state, ownProps),
    newlyAdded: newlyAddedSelector(state, ownProps),
    color: colorSelector(state, ownProps),
    lensIds: lensIdSelector(state, ownProps),
    fixedLensIds: fixedLensIdSelector(state, ownProps),
    model: modelSelector(state, ownProps),
    map: mapRefSelector(state),
    camerasCount: cameraCountSelector(state),
    activeInUI: cameraSelectedSelector(state, ownProps),
    lensSelected: lensSelectedSelector(state, ownProps),
    cameraViewsChoice: cameraViewsChoiceSelector(state),
    showSimpleCameraIcon: showSimpleCameraIconSelector(state),
    drawing: drawingWallSelector(state) || drawingWireSelector(state),
    wireSelected: wireSelectedSelector(state, { id: ownProps.id, objType: 'camera' })
  })
  return mapStateToProps
}

class MultiImager extends React.Component {
  constructor(props) {
    super(props)
    const { location, map } = this.props
    const latLngLocation = new google.maps.LatLng(location)

    this.state = {
      markerPosition: isNaN(latLngLocation.lat()) ? map.getCenter() : latLngLocation,
      dragging: false
    }

    this.markers = {}
  }

  componentDidMount() {
    const { remove, props: { id, updateCalculatorAction, location, addMapComponentReferencesAction, newlyAdded, map, classes } } = this

    this.contextMenu = new ContextMenu(map, this.contextMenuOptions(classes))

    this.markers.camera.addListener('contextmenu', showContextMenu(this.contextMenu))
    google.maps.event.addListener(this.contextMenu, 'menu_item_selected', this.eventHandler)

    google.maps.event.addDomListener(this.markers.camera, 'dblclick', this.showRenameCameraOverlay)
    newlyAdded && !onMobile() && this.showRenameCameraOverlay({ autoclose: true })

    this.markers.camera.addListener('position_changed', this.move)
    this.markers.camera.addListener('mousedown', this.setCurrentIfApplicable)

    addMapComponentReferencesAction('cameras', id, { remove, markers: this.extractMarkersFromComponents() })
    if (!location) updateCalculatorAction(this.state.markerPosition, 'location', id, true)
  }

  setCurrentIfApplicable = _ => {
    const { drawing, active, camerasCount, activeInUI, changeCameraAction, updateCurrentEntityAction, id } = this.props
    if (drawing) return
    if (!activeInUI) updateCurrentEntityAction({ id, type: 'camera' })
    if (!active && camerasCount > 1) changeCameraAction(id)
  }

  extractMarkersFromComponents = _ =>
    Object.assign(
      {
        camera: this.markers.camera,
        cameraOutline: this.markers.cameraOutline
      }
    )

  contextMenuOptions(classes) {
    const menuItemBuilder = function() {
      const { props: { locked } } = this
      const items = [
        {
          className: classes.contextMenuItem,
          eventName: 'renameCamera',
          id: 'menu_item_rename_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon='pen' />)} Rename Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'goToCamera',
          id: 'menu_item_go_to_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'dot-circle']} />)} Go To Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'duplicateCamera',
          id: 'menu_item_duplicate_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(COPY_ICON_STACK)} Duplicate Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'lockCamera',
          id: 'menu_item_lock_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={locked ? 'unlock' : 'lock'} />)} ${(locked ? 'Unlock' : 'Lock')} Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'deleteCamera',
          id: 'menu_item_delete_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'trash-alt']} />)} Delete Camera`
        }
      ]

      return items
    }
    return {
      classNames: {
        menu: classes.contextMenu,
        menuSeparator: classes.contextMenuSeparator
      },
      menuItems: menuItemBuilder.bind(this)
    }
  }

  eventHandler = (_, eventName) => {
    const { props: { locked, id, lensIds } } = this
    switch (eventName) {
      case 'renameCamera':
        this.showRenameCameraOverlay()
        break
      case 'goToCamera':
        const camera = { isMultiImager: true, lensIds }
        this.props.zoomToSingleCameraAction(camera)
        break
      case 'lockCamera':
        this.props.updateCalculatorAction(!locked, 'locked', id)
        break
      case 'deleteCamera':
        if (locked) {
          this.props.messageAction('generalError', 'Cannot delete locked camera. Right click to unlock.')
        } else if (window.confirm(`Are you sure you want to delete ${this.props.name} and all imagers?`)) {
          this.props.destroyMultiImagerAction(id)
        }
        break
      case 'duplicateCamera':
        this.props.checkOfflineLimitsAction('copyMultiImager', [id])
        break
    }
    return this.contextMenu.hide()
  }

  showRenameCameraOverlay = ({ autoclose } = {}) => {
    const { map, id, name } = this.props
    new RenameCameraOverlay({
      map,
      initialTitle: name,
      markerPosition: this.markers.camera.getPosition(),
      autoClose: autoclose || false,
      callback: name => this.props.updateCalculatorAction(name, 'name', id, true)
    })
  }

  move = _ => {
    this.setState({ markerPosition: this.markers.camera.getPosition() })
  }

  remove = _ => {
    const markers = this.extractMarkersFromComponents()
    Object.values(markers)
      .filter(m => !!m)
      .forEach(m => m.setMap(null))
  }

  setDragging = dragging => {
    if (this.state.dragging !== dragging) this.setState({ dragging })
  }

  addToMarkers = (key, value) => this.markers[key] = value

  updateLensHeadings = (targetId, heading, distance) => {
    const { fixedLensIds, batchUpdateCameraAction } = this.props
    const index = fixedLensIds.indexOf(targetId)

    fixedLensIds.forEach((id, i) => {
      if (id !== targetId) batchUpdateCameraAction(['heading', 'distance'], { heading: heading + (i - index) * 360 / fixedLensIds.length, distance }, id)
    })
  }

  render() {
    const { addToMarkers, setDragging, updateLensHeadings, state: { markerPosition, dragging },
      props: { map, color, locked, name, id, activeInUI, updateCalculatorAction, messageAction, lensIds, lensSelected, model, cameraViewsChoice,
      showSimpleCameraIcon, drawingWire, wireSelected } } = this
    const displayColor = activeInUI || lensSelected || wireSelected || cameraViewsChoice !== 'All Cameras' ? CAMERA_SELECTED_COLORS[color] : CAMERA_COLORS[color]

    return (
      <div>
        { lensIds.map(lensId =>
            <Camera key={'lens' + lensId + model.id} isLens { ...{ id: lensId, position: markerPosition, selectionOverride: activeInUI || wireSelected, draggingOverride: dragging, updateLensHeadings, setDragging } } />
          )
        }
        <Marker { ...{ position: markerPosition, map, color: displayColor, locked, name, id, addToMarkers, setDragging, updateCalculatorAction, messageAction, lensIds, showSimpleCameraIcon, drawingWire } } />
      </div>
    )
  }
}

export default connect(makeMapStateToProps, { updateCalculatorAction, zoomToSingleCameraAction, destroyMultiImagerAction, changeCameraAction, batchUpdateCameraAction,
  addMapComponentReferencesAction, messageAction, updateCurrentEntityAction, checkOfflineLimitsAction })(injectSheet(style)(MultiImager))



// WEBPACK FOOTER //
// ./src/components/MultiImager/index.js